import { useState } from 'react';

import { CardGridSkeleton } from '@/components/common/card-grid-skeleton';
import { PaginationFooter } from '@/components/data/data-table';

import { BUNDLE_TIER_LABEL } from '../format';
import { useApplicableEntities } from '../hooks';

import styles from './discount-entities-tab.module.scss';
import { DiscountEntityCard } from './discount-entity-card';

// Must be one of the backend's applicable-entities `limit` enum values (10/20/30/40/50).
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];

export type DiscountEntitiesTabProps = {
  discountId: string;
  type: 'device' | 'bundle';
  search: string;
};

/** Device/Bundle relationship tab — a bespoke 2-col card grid (ADR-0012, not a DataTable) over
 *  the discount's applicable entities, paged 8/tile-page and filtered by the header search. */
export function DiscountEntitiesTab({ discountId, type, search }: DiscountEntitiesTabProps) {
  const [page, setPage] = useState(1);
  // 117324: the backend's `limit` enum offers 10/20/30/40/50 — this tab only ever asked for 10.
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Reset to page 1 when the search term changes (adjust state during render — the
  // React-recommended alternative to a setState-in-effect).
  const [lastSearch, setLastSearch] = useState(search);
  if (search !== lastSearch) {
    setLastSearch(search);
    setPage(1);
  }

  const { data, isPending } = useApplicableEntities(discountId, type, {
    page,
    limit: pageSize,
    search,
  });

  if (isPending || !data) return <CardGridSkeleton count={pageSize} />;

  if (data.data.length === 0) return <p className={styles.empty}>No {type}s found</p>;

  return (
    <>
      <div className={styles.grid}>
        {data.data.map((entity) => (
          <DiscountEntityCard
            key={entity.id}
            imageUrl={entity.imageUrl}
            name={entity.name}
            subtitle={'tier' in entity ? BUNDLE_TIER_LABEL[entity.tier] : entity.category}
          />
        ))}
      </div>
      <PaginationFooter
        page={page}
        pageSize={pageSize}
        total={data.meta.totalItems}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onChange={(nextPage, nextPageSize) => {
          setPage(nextPage);
          setPageSize(nextPageSize);
        }}
      />
    </>
  );
}
